import React, { ComponentType, useState } from 'react';
import { Box, Container, Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { getRedirectPath, clearRedirectPath } from '@/utils/redirectPath';
import { useRouter } from 'next/router';
import useTheme from '@/theme/useTheme';
import CatchError from '@/components/system/CacthError';
import dynamic from 'next/dynamic';
import Loading from '@/components/ui/Loading';
import LocalizedLink from '@/components/ui/LocalizedLink';
import ParticleBackground from '@/components/ui/ParticleBackground';
import LoginCard from '@/components/system/Auth/LoginCard';

// Dynamically import components
const LoginForm = dynamic(
  () => import('@/components/system/Auth/LoginPage/LoginForm'),
  {
    ssr: false,
    loading: () => <Loading size={120} />,
  },
);

const ResetPasswordForm: ComponentType<{
  onBackToLogin?: () => void;
  onResetSuccess?: () => void;
}> = dynamic(() => import('@/components/system/Auth/AuthModal/ResetPassword'), {
  ssr: false,
  loading: () => <Loading size={120} />,
});

// Login page component
const LoginPage = () => {
  const { t } = useTranslation(['auth', 'common']);
  const { theme } = useTheme();
  const router = useRouter();

  // State for showing reset password form
  const [showResetPassword, setShowResetPassword] = useState(false);

  return (
    <Box
      sx={{
        mt: { xs: 0, md: 6 },
        minHeight: '60vh',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <LoginCard mode="login">
        {showResetPassword ? (
          <>
            <Box display="flex" justifyContent="center" alignItems="center">
              <Typography
                variant="h5"
                component="h2"
                mb={3}
                fontWeight={600}
                fontSize={{ xs: '1.125rem', sm: '1.25rem' }}
                color="text.secondary"
                align="center"
              >
                {t('login.resetPassword.title')}
              </Typography>
            </Box>
            <ResetPasswordForm
              onBackToLogin={() => setShowResetPassword(false)}
              onResetSuccess={() => setShowResetPassword(false)}
            />
          </>
        ) : (
          <LoginForm
            onResetPasswordClick={() => setShowResetPassword(true)}
            onLoginSuccess={() => {
              // Get the saved redirect path and clear it
              const redirectPath = getRedirectPath();
              clearRedirectPath();
              router.push(redirectPath);
            }}
          />
        )}
      </LoginCard>
    </Box>
  );
};

export async function getServerSideProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common', 'auth'])),
    },
  };
}

export default CatchError(LoginPage);
